import { useCallback } from "react";
import { toast } from "@jsq/ds/core";
import { BaseDocumentType, ValueTypeEnum } from "~/graphql/supergraphEnums";
import { useUpdateComplianceDocumentDetailsMutation } from "~/graphql/supergraphHooks";
import { type DocumentValueType } from "~/graphql/supergraphTypes";
import { type TaxFormType, type TaxFormValues } from "../types";
import { transformTaxFormValuesToMetadata } from "../utils/taxFormMetadataTransformer";

const TAX_FORM_TO_DOCUMENT_PURPOSE: Record<TaxFormType, BaseDocumentType> = {
  W9: BaseDocumentType.WSeriesTaxForm,
  W8BEN: BaseDocumentType.WSeriesTaxForm,
  W8BENE: BaseDocumentType.WSeriesTaxForm,
  W8ECI: BaseDocumentType.WSeriesTaxForm,
  W8EXP: BaseDocumentType.WSeriesTaxForm,
  W8IMY: BaseDocumentType.WSeriesTaxForm,
  WITHHOLDING_STATEMENT: BaseDocumentType.WSeriesTaxForm,
  CRS_INDIVIDUAL: BaseDocumentType.SelfCertification,
  CRS_ENTITY: BaseDocumentType.SelfCertification,
};

type UseTaxFormSaveProps = {
  caseId: number;
  documentId: number | undefined;
  formType: TaxFormType;
  profileId?: number | null;
  existingMetadata?: DocumentValueType[] | null;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
};

type UseTaxFormSaveReturn = {
  saveTaxForm: (values: TaxFormValues) => Promise<boolean>;
  loading: boolean;
};

export const useTaxFormSave = ({
  caseId,
  documentId,
  formType,
  profileId,
  existingMetadata,
  onSuccess,
  onError,
}: UseTaxFormSaveProps): UseTaxFormSaveReturn => {
  const [updateDocumentDetails, { loading }] = useUpdateComplianceDocumentDetailsMutation({
    refetchQueries: ["GetComplianceCase"],
    onCompleted: () => {
      toast.success("Tax form saved successfully");
      onSuccess?.();
    },
    onError: (error) => {
      toast.error(error.message);
      onError?.(error);
    },
  });

  const saveTaxForm = useCallback(
    async (values: TaxFormValues): Promise<boolean> => {
      if (!documentId || !caseId) {
        toast.error("Missing document or case information");
        return false;
      }

      const metadata = transformTaxFormValuesToMetadata(formType, values);
      const existingDate = existingMetadata?.find((item) => item?.key === "date");
      if (existingDate?.value) {
        metadata.push({ key: "date", value: existingDate.value, type: ValueTypeEnum.Date });
      }

      const documentPurpose = TAX_FORM_TO_DOCUMENT_PURPOSE[formType];

      try {
        await updateDocumentDetails({
          variables: {
            caseId,
            documentId,
            updateInfo: {
              profileId,
              metadata,
              documentPurpose,
            },
          },
        });
        return true;
      } catch {
        return false;
      }
    },
    [caseId, documentId, formType, profileId, existingMetadata, updateDocumentDetails],
  );

  return { saveTaxForm, loading };
};
